import { getUserProfile, updateUserProfile } from '@app/api/user.api';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { notificationController } from '@app/controllers/notificationController';
import { Col, Form, Row, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import { UserDTO } from '@app/models/user/UserDTO';
import { DropdownOption } from '@app/models/DropdownOption';
import { RegularCustomerBusinessInfo } from './components/RegularCustomerBusinessInfo';
import { UserInfo } from './components/UserInfo';
import { authUserService } from '@app/services/authUser.service';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { useTranslation } from 'react-i18next';

type AccountInformationProps = Record<string, unknown>;

const AccountInformation: React.FC<AccountInformationProps> = () => {
  const [form] = Form.useForm();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const { t } = useTranslation();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [accountTypes, setAccountTypes] = useState<DropdownOption[]>([]);
  const [companyTypes, setCompanyTypes] = useState<DropdownOption[]>([]);
  const [isCustomer, setIsCustomer] = useState<boolean>(false);
  const [isRegularCustomer, setIsRegularCustomer] = useState<boolean>(false);
  const [isBusinessRegularCustomer, setIsBusinessRegularCustomer] = useState<boolean>(false);
  const [isBusinessRegularCustomerRegistered, setIsBusinessRegularCustomerRegistered] = useState<boolean>(false);

  const setApiData = (responseData: UserDTO) => {
    setIsCustomer(responseData?.user?.usertype == 'CUSTOMER');
    setIsRegularCustomer(responseData?.user?.customer?.customer_type == 'REGULAR');
    setIsBusinessRegularCustomer(responseData?.user?.customer?.additional_info?.business_type == 'BUSINESS');
    setAccountTypes(responseData?.business_type_options ?? []);
    setCompanyTypes(responseData?.company_type_options ?? []);
    setIsBusinessRegularCustomerRegistered(responseData?.user?.customer?.additional_info?.business_reg_no?.length != 0);

    const companyType =
      mode == 'view'
        ? responseData?.company_type_options?.find(
            (e) => e.value == responseData?.user?.customer?.additional_info?.company_type,
          )?.label
        : responseData?.user?.customer?.additional_info?.company_type;

    const businessType =
      mode == 'view'
        ? responseData?.business_type_options?.find(
            (e) => e.value == responseData?.user?.customer?.additional_info?.business_type,
          )?.label
        : responseData?.user?.customer?.additional_info?.business_type;

    const isRegisteredValue =
      responseData?.user?.customer?.additional_info?.business_reg_no?.length != 0 ? 'Yes' : 'No';
    const isRegistered =
      mode == 'view' ? isRegisteredValue : responseData?.user?.customer?.additional_info?.business_reg_no?.length != 0;

    const values = {
      first_name: responseData?.user?.customer?.first_name,
      email: responseData?.user?.email,
      username: responseData?.user?.username,
      last_name: responseData?.user?.customer?.last_name,
      dob: responseData?.user?.customer?.dob,
      contact_no: responseData?.user?.customer?.contact_no,
      address: responseData?.user?.customer?.address,
      nic: responseData?.user?.customer?.nic,
      customer_type: responseData?.user?.customer?.customer_type,
      business_type: businessType,
      business_name: responseData?.user?.customer?.additional_info?.business_name,
      business_reg_no: responseData?.user?.customer?.additional_info?.business_reg_no,
      is_registered: isRegistered,
      business_address: responseData?.user?.customer?.additional_info?.business_address,
      company_type: companyType,
    };

    form.setFieldsValue(values);
  };

  const getUserProfileData = () => {
    const user = authUserService.getUser();
    const id = user?.id ?? 0;

    getUserProfile(id)
      .then((response) => {
        console.log(response);
        setApiData(response);
      })
      .catch((error) => {
        notificationController.error({ message: error.message });
      });
  };

  useEffect(() => {
    getUserProfileData();
  }, []);

  const close = () => {
    setMode('view');
  };

  useEffect(() => {
    if (mode === 'edit') {
      addHeaderButton(
        'Save',
        <BaseButton htmlType="submit" type="primary" size="small" form="accountForm">
          Save
        </BaseButton>,
      );
      addHeaderButton(
        'Cancel',
        <BaseButton htmlType="button" type="primary" size="small" onClick={close}>
          Cancel
        </BaseButton>,
      );
    } else {
      addHeaderButton(
        'Edit',
        <BaseButton type="primary" size="small" onClick={() => setMode('edit')}>
          Edit
        </BaseButton>,
      );

      removeHeaderButton('Save');
      removeHeaderButton('Cancel');
    }

    return () => {
      removeHeaderButton('Save');
      removeHeaderButton('Cancel');
      removeHeaderButton('Edit');
    };
  }, [mode, addHeaderButton, removeHeaderButton]);

  const onFinish = (values: any) => {
    const user = authUserService.getUser();
    const id = user?.id ?? 0;

    const req = {
      ...values,
      customer_type: user?.customer?.customer_type,
    };

    console.log(req);

    updateUserProfile(id, req)
      .then((response) => {
        notificationController.success({ message: 'Profile updated successfully!' });
        close();
        setApiData(response?.data);
      })
      .catch((error) => notificationController.error({ message: error.message }));
  };

  return (
    <BaseCard
      title="Account Information"
      // extra={
      //   mode == 'view' && (
      //     <BaseButton type="primary" onClick={() => setMode('edit')}>
      //       Edit
      //     </BaseButton>
      //   )
      // }
    >
      <Form
        form={form}
        labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
        labelAlign="left"
        labelWrap
        wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
        size="small"
        onFinish={onFinish}
      >
        <UserInfo mode={mode} />
        {isCustomer && isRegularCustomer && (
          <RegularCustomerBusinessInfo
            isRegistered={isBusinessRegularCustomerRegistered}
            accountTypes={accountTypes}
            company_types={companyTypes}
            mode={mode}
          />
        )}
      </Form>
    </BaseCard>
  );
};

export default AccountInformation;
